'use client';
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
	id: string;
	message: string;
	type: ToastType;
	onDismiss: (id: string) => void;
}

const icons = {
	success: <CheckCircle className="w-5 h-5 text-green-500" />,
	error: <AlertCircle className="w-5 h-5 text-red-500" />,
	warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
	info: <Info className="w-5 h-5 text-blue-500" />,
};

export const Toast: React.FC<ToastProps> = ({ id, message, type, onDismiss }) => {
	useEffect(() => {
		const timer = setTimeout(() => {
			onDismiss(id);
		}, 5000);
		return () => clearTimeout(timer);
	}, [id, onDismiss]);

	return (
		<motion.div
			initial={{ opacity: 0, y: 50, scale: 0.9 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
			className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-lg rounded-xl p-4 flex items-center gap-3 min-w-[300px] max-w-md pointer-events-auto"
			role="alert"
		>
			<div className="flex-shrink-0">{icons[type]}</div>
			<p className="flex-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
				{message}
			</p>
			<button
				onClick={() => onDismiss(id)}
				className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
			>
				<X className="w-4 h-4 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300" />
			</button>
		</motion.div>
	);
};
